import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de as deLocale, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { foldSearch } from '@eis/core/reporting';

/** The catalog types a filter value can have (`ReportFieldType`). */
export type FilterValueType = 'STRING' | 'NUMBER' | 'DATE' | 'DATETIME' | 'BOOLEAN';

/** How a DATETIME value is entered and read: the browser's zone, or UTC. Stored values are UTC. */
export type FilterTimeZone = 'LOCAL' | 'UTC';

/** The UI-only range operator: the document stores it as `>= from` and `<= to` (two clauses). */
export const RANGE_OP = '[]';

const LIST_OPS = new Set(['=()', '!()']);
const NULL_OPS = new Set(['==$null', '!=$null']);
const NUMBER = /^-?\d+([.,]\d+)?$/;

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** An RFC 3339 instant (`…Z`) as the wall-clock `Date` the picker shows in `zone`. */
export function instantToPicker(value: string, zone: FilterTimeZone): Date | null {
  if (value === '') return null;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  if (zone === 'LOCAL') return at;
  return new Date(
    at.getUTCFullYear(),
    at.getUTCMonth(),
    at.getUTCDate(),
    at.getUTCHours(),
    at.getUTCMinutes(),
  );
}

/** The picker's wall-clock `Date`, read in `zone`, as the RFC 3339 instant the document stores. */
export function pickerToInstant(value: Date | null, zone: FilterTimeZone): string {
  if (value === null || Number.isNaN(value.getTime())) return '';
  const utc =
    zone === 'LOCAL'
      ? value
      : new Date(
          Date.UTC(
            value.getFullYear(),
            value.getMonth(),
            value.getDate(),
            value.getHours(),
            value.getMinutes(),
          ),
        );
  return `${utc.toISOString().slice(0, 16)}:00Z`;
}

const civilToDate = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m === null ? null : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const dateToCivil = (value: Date | null): string =>
  value === null || Number.isNaN(value.getTime())
    ? ''
    : `${String(value.getFullYear()).padStart(4, '0')}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;

/** A typed number in the document's form: decimal point, nothing else. */
export function normalizeNumber(value: string): string {
  return value.trim().replace(',', '.');
}

/** True when every value the operator needs is present and of the field's type. */
export function filterValuesComplete(
  type: FilterValueType,
  op: string,
  values: readonly string[],
): boolean {
  if (NULL_OPS.has(op)) return true;
  const present = values.filter((v) => v.trim() !== '');
  if (op === RANGE_OP) {
    if (present.length !== 2) return false;
  } else if (LIST_OPS.has(op)) {
    if (present.length === 0) return false;
  } else if (present.length !== 1) return false;
  return type !== 'NUMBER' || present.every((v) => NUMBER.test(v.trim()));
}

export interface FilterValueInputProps {
  readonly type: FilterValueType;
  readonly op: string;
  /** One value, the list's values, or `[from, to]` for {@link RANGE_OP}. */
  readonly values: readonly string[];
  readonly onChange: (values: string[]) => void;
  /** A STRING field's values, most frequent first, optionally by prefix (SQL-6). */
  readonly loadValues?: (prefix: string) => Promise<readonly string[]>;
  readonly timeZone?: FilterTimeZone;
  /** Shows the zone switch next to a DATETIME input. */
  readonly onTimeZoneChange?: (zone: FilterTimeZone) => void;
  /** A message under the input (a missing value). */
  readonly error?: string;
}

/**
 * A filter's value, in the control its type wants (owner feedback 2026-11-01): a searchable
 * checkbox list for "ist eines von" on text (like a spreadsheet's column filter), a calendar for a
 * date, a calendar with time and the zone (Lokal / UTC — the warehouse stores UTC) for a date-time,
 * two of them for a range, numbers only for a number, Ja / Nein for a flag. The values come back
 * in the document's form: civil `YYYY-MM-DD`, RFC 3339 `…Z`, a decimal with a point, `true`/`false`.
 *
 * A DATE here is a data date ("policies starting after"), not an effective date the user reads the
 * system at — the plain picker is the right control (the PropertyFilter reasoning, CLAUDE.md rule 6).
 */
export function FilterValueInput(props: FilterValueInputProps): ReactNode {
  const { i18n } = useTranslation('patterns');
  const { type, op } = props;
  if (NULL_OPS.has(op)) return null;
  const dated = type === 'DATE' || type === 'DATETIME';
  const body =
    type === 'BOOLEAN' ? (
      <BooleanInput {...props} />
    ) : LIST_OPS.has(op) && type === 'STRING' ? (
      <ValueChecklist {...props} />
    ) : LIST_OPS.has(op) ? (
      <ListInput {...props} />
    ) : dated ? (
      <DateInput {...props} />
    ) : type === 'NUMBER' ? (
      <NumberInput {...props} />
    ) : (
      <TextInput {...props} />
    );
  return dated ? (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={i18n.language.startsWith('de') ? deLocale : enGB}
    >
      {body}
    </LocalizationProvider>
  ) : (
    body
  );
}

function BooleanInput({ values, onChange }: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={values[0] ?? ''}
      aria-label={t('reporting.filterInput.value')}
      onChange={(_, next: string | null) => {
        if (next !== null) onChange([next]);
      }}
    >
      <ToggleButton value="true">{t('reporting.filterInput.true')}</ToggleButton>
      <ToggleButton value="false">{t('reporting.filterInput.false')}</ToggleButton>
    </ToggleButtonGroup>
  );
}

/** Values of a STRING field as they are typed, with suggestions, for the single-value operators. */
function TextInput({ values, onChange, loadValues, error }: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  const suggestions = useSuggestions(loadValues, values[0] ?? '');
  return (
    <Autocomplete
      freeSolo
      size="small"
      options={suggestions.values}
      inputValue={values[0] ?? ''}
      onInputChange={(_, text) => {
        onChange([text]);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('reporting.filterInput.value')}
          error={error !== undefined}
          {...(error === undefined ? {} : { helperText: error })}
        />
      )}
    />
  );
}

/** Suggestions by prefix, debounced; the last answer wins. */
function useSuggestions(
  loadValues: FilterValueInputProps['loadValues'],
  prefix: string,
): { values: string[]; loading: boolean } {
  const [state, setState] = useState<{ values: string[]; loading: boolean }>({
    values: [],
    loading: loadValues !== undefined,
  });
  useEffect(() => {
    if (loadValues === undefined) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      setState((s) => ({ ...s, loading: true }));
      loadValues(prefix)
        .then((found) => {
          if (live) setState({ values: [...found], loading: false });
        })
        .catch(() => {
          if (live) setState({ values: [], loading: false });
        });
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [loadValues, prefix]);
  return state;
}

/**
 * "ist eines von" on text: a search field over the field's values and a checkbox per value — the
 * chosen ones stay on top — plus "„…“ hinzufügen" for a value the suggestions do not list.
 */
function ValueChecklist({ values, onChange, loadValues, error }: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [search, setSearch] = useState('');
  const suggestions = useSuggestions(loadValues, search.trim());
  const chosen = new Set(values);
  const terms = foldSearch(search.trim());
  const matches = (v: string): boolean => {
    const text = foldSearch(v).join(' ');
    return terms.every((term) => text.includes(term));
  };
  const shown = useMemo(() => {
    const extra = suggestions.values.filter((v) => !chosen.has(v));
    return [...values.filter(matches), ...extra.filter(matches)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, suggestions.values, search]);
  const typed = search.trim();
  const toggle = (value: string, on: boolean): void => {
    onChange(on ? [...values, value] : values.filter((v) => v !== value));
  };
  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        value={search}
        placeholder={t('reporting.filterInput.search')}
        slotProps={{
          htmlInput: { 'aria-label': t('reporting.filterInput.search') },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && typed !== '') {
            event.preventDefault();
            if (!chosen.has(typed)) toggle(typed, true);
            setSearch('');
          }
        }}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button
          size="small"
          disabled={shown.length === 0}
          onClick={() => {
            onChange([...values, ...shown.filter((v) => !chosen.has(v))]);
          }}
        >
          {t('reporting.filterInput.selectVisible')}
        </Button>
        <Button
          size="small"
          disabled={values.length === 0}
          onClick={() => {
            onChange([]);
          }}
        >
          {t('reporting.filterInput.clear')}
        </Button>
        <Typography
          variant="caption"
          color={error === undefined ? 'text.secondary' : 'error'}
          sx={{ ml: 'auto' }}
          role={error === undefined ? undefined : 'alert'}
        >
          {error ?? t('reporting.filterInput.selected', { count: values.length })}
        </Typography>
      </Stack>
      <Box
        role="group"
        aria-label={t('reporting.filterInput.values')}
        sx={{
          maxHeight: 240,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: 1,
        }}
      >
        {typed !== '' && !shown.includes(typed) && (
          <Button
            size="small"
            onClick={() => {
              toggle(typed, true);
              setSearch('');
            }}
          >
            {t('reporting.filterInput.addTyped', { value: typed })}
          </Button>
        )}
        {shown.length === 0 && typed === '' && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {suggestions.loading
              ? t('reporting.filterInput.loading')
              : t('reporting.filterInput.noValues')}
          </Typography>
        )}
        {shown.map((value) => (
          <Box key={value}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={chosen.has(value)}
                  onChange={(event) => {
                    toggle(value, event.target.checked);
                  }}
                />
              }
              label={value}
            />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

/** "ist eines von" on a number or a date: chips, each checked against the type. */
function ListInput({ type, values, onChange, error }: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [pending, setPending] = useState('');
  const [bad, setBad] = useState(false);
  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={[]}
      value={[...values]}
      inputValue={pending}
      onInputChange={(_, text) => {
        setPending(text);
        setBad(false);
      }}
      onChange={(_, next) => {
        const cleaned = next.map((v) => (type === 'NUMBER' ? normalizeNumber(v) : v.trim()));
        if (type === 'NUMBER' && cleaned.some((v) => !NUMBER.test(v))) {
          setBad(true);
          return;
        }
        onChange(cleaned.filter((v) => v !== ''));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('reporting.filterInput.values')}
          error={bad || error !== undefined}
          helperText={
            bad
              ? t('reporting.filterInput.notANumber')
              : (error ?? t('reporting.filterInput.valuesHint'))
          }
        />
      )}
    />
  );
}

/** A number, or a range of two: anything but digits, one sign and one decimal mark is refused. */
function NumberInput({ op, values, onChange, error }: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  const field = (label: string, index: number): ReactNode => {
    const current = values[index] ?? '';
    const invalid = current !== '' && !NUMBER.test(current);
    return (
      <TextField
        size="small"
        label={label}
        value={current}
        slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        error={invalid || (error !== undefined && current === '')}
        {...(invalid
          ? { helperText: t('reporting.filterInput.notANumber') }
          : error !== undefined && current === ''
            ? { helperText: error }
            : {})}
        onChange={(event) => {
          const typed = event.target.value.replace(/[^\d.,-]/g, '');
          const next = [...values];
          while (next.length <= index) next.push('');
          next[index] = typed;
          onChange(next.map((v) => v.replace(',', '.')));
        }}
        sx={{ flex: 1 }}
      />
    );
  };
  return op === RANGE_OP ? (
    <Stack direction="row" spacing={1}>
      {field(t('reporting.filterInput.from'), 0)}
      {field(t('reporting.filterInput.to'), 1)}
    </Stack>
  ) : (
    field(t('reporting.filterInput.number'), 0)
  );
}

/** A calendar (DATE) or a calendar with time and the zone switch (DATETIME); two for a range. */
function DateInput({
  type,
  op,
  values,
  onChange,
  timeZone = 'LOCAL',
  onTimeZoneChange,
  error,
}: FilterValueInputProps): ReactNode {
  const { t } = useTranslation('patterns');
  const set = (index: number, value: string): void => {
    const next = [...values];
    while (next.length <= index) next.push('');
    next[index] = value;
    onChange(next);
  };
  const picker = (label: string, index: number): ReactNode => {
    const current = values[index] ?? '';
    const slot = {
      textField: {
        size: 'small' as const,
        fullWidth: true,
        error: error !== undefined && current === '',
        ...(error !== undefined && current === '' ? { helperText: error } : {}),
      },
    };
    return type === 'DATETIME' ? (
      <DateTimePicker
        label={label}
        ampm={false}
        value={instantToPicker(current, timeZone)}
        onChange={(next) => {
          set(index, pickerToInstant(next, timeZone));
        }}
        slotProps={slot}
      />
    ) : (
      <DatePicker
        label={label}
        value={civilToDate(current)}
        onChange={(next) => {
          set(index, dateToCivil(next));
        }}
        slotProps={slot}
      />
    );
  };
  return (
    <Stack spacing={1.5}>
      {op === RANGE_OP ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {picker(t('reporting.filterInput.from'), 0)}
          {picker(t('reporting.filterInput.to'), 1)}
        </Stack>
      ) : (
        picker(t('reporting.filterInput.value'), 0)
      )}
      {type === 'DATETIME' && onTimeZoneChange !== undefined && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={timeZone}
            aria-label={t('reporting.filterInput.timeZone')}
            onChange={(_, next: FilterTimeZone | null) => {
              if (next !== null) onTimeZoneChange(next);
            }}
          >
            <ToggleButton value="LOCAL">
              {t('reporting.filterInput.timeZoneLocal', { zone: browserTimeZone() })}
            </ToggleButton>
            <ToggleButton value="UTC">{t('reporting.filterInput.timeZoneUtc')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {t('reporting.filterInput.timeZoneHint')}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
